import { IMainCart } from "@sections/main-cart";

class CartItem extends HTMLElement {
  private quantityDecreaseButton?: HTMLButtonElement;
  private quantityIncreaseButton?: HTMLButtonElement;
  private quantityInput?: HTMLInputElement;
  private removeButton?: HTMLButtonElement;
  private errorMessage?: HTMLElement;

  constructor() {
    super();
    this.handleItemUpdateDecrease = this.handleItemUpdateDecrease.bind(this);
    this.handleItemUpdateIncrease = this.handleItemUpdateIncrease.bind(this);
    this.handleItemUpdateRemove = this.handleItemUpdateRemove.bind(this);
  }

  connectedCallback(): void {
    this.handleInitialization();
  }

  disconnectedCallback(): void {
    this.cleanupEventListeners();
    this.destroyInstance();
  }

  handleInitialization(): void {
    this.cleanupEventListeners();
    this.cacheDomElements();
    this.setEventListeners();
  }

  cacheDomElements(): void {
    this.quantityDecreaseButton = this.querySelector(
      '[js-cart-item="decrease"]',
    ) as HTMLButtonElement;
    this.quantityIncreaseButton = this.querySelector(
      '[js-cart-item="increase"]',
    ) as HTMLButtonElement;
    this.quantityInput = this.querySelector(
      '[js-cart-item="quantityInput"]',
    ) as HTMLInputElement;
    this.removeButton = this.querySelector(
      '[js-cart-item="remove"]',
    ) as HTMLButtonElement;
    this.errorMessage = this.querySelector(
      '[js-cart-item="errorMessage"]',
    ) as HTMLElement;
  }

  setEventListeners(): void {
    // Ensure listeners are not duplicated
    this.cleanupEventListeners();

    if (this.quantityDecreaseButton) {
      this.quantityDecreaseButton.addEventListener(
        "click",
        this.handleItemUpdateDecrease,
      );
    }

    if (this.quantityIncreaseButton) {
      this.quantityIncreaseButton.addEventListener(
        "click",
        this.handleItemUpdateIncrease,
      );
    }

    if (this.removeButton) {
      this.removeButton.addEventListener("click", this.handleItemUpdateRemove);
    }
  }

  cleanupEventListeners(): void {
    if (this.quantityDecreaseButton) {
      this.quantityDecreaseButton.removeEventListener(
        "click",
        this.handleItemUpdateDecrease,
      );
    }

    if (this.quantityIncreaseButton) {
      this.quantityIncreaseButton.removeEventListener(
        "click",
        this.handleItemUpdateIncrease,
      );
    }

    if (this.removeButton) {
      this.removeButton.removeEventListener(
        "click",
        this.handleItemUpdateRemove,
      );
    }
  }

  handleItemUpdateDecrease(): void {
    this.handleItemUpdate("DECREASE");
  }

  handleItemUpdateIncrease(): void {
    this.handleItemUpdate("INCREASE");
  }

  handleItemUpdateRemove(): void {
    this.handleItemUpdate("REMOVE");

    const event = new CustomEvent("cart-item:removed");
    document.dispatchEvent(event);
  }

  handleItemUpdate(action: string): void {
    const itemJson = this.getAttribute("item-json") as string;
    const itemData = JSON.parse(itemJson);

    const step = parseInt(this.quantityInput?.getAttribute("step") || "1");

    const max = parseInt(this.quantityInput?.getAttribute("max") || "100");
    const min = parseInt(this.quantityInput?.getAttribute("min") || "1");

    const id = itemData.key;
    let newQuantity = itemData.quantity;

    if (newQuantity >= max && action === "INCREASE") {
      const maxQuantityMessage =
        window.themeVars.strings.product.maxQuantityReached;
      if (this.errorMessage && this.errorMessage.classList.contains("hidden")) {
        this.errorMessage.innerHTML = `${maxQuantityMessage} ${itemData.title}`;
        this.errorMessage.classList.remove("hidden");
        setTimeout(() => {
          this.errorMessage?.classList.add("hidden");
        }, 3000);
      }
      return;
    }

    if (newQuantity <= min && action === "DECREASE") {
      return;
    }

    switch (action) {
      case "DECREASE":
        newQuantity -= step;
        break;

      case "INCREASE":
        newQuantity += step;
        break;

      case "REMOVE":
        newQuantity = 0;
        break;
    }

    const mainCart = this.closest("uwp-main-cart") as IMainCart;

    if (!mainCart) {
      console.error("Cart: Cart section not found.");
      return;
    }

    mainCart.changeCartItemQuantity(id, newQuantity);
    mainCart.handleUpdateCartSection();
  }

  destroyInstance(): void {
    this.quantityDecreaseButton = undefined;
    this.quantityIncreaseButton = undefined;
    this.removeButton = undefined;
  }
}

customElements.define("uwp-cart-item", CartItem);
